// Upload: (photos resized on the device) → signed URL → straight to private storage → recorded (audited).
import { api } from './api';
import { supabase } from './supabase';

const MAX_EDGE = 2048;

async function sha256(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function resizeImage(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  if (scale === 1 && file.type === 'image/jpeg') {
    bmp.close();
    return { blob: file, width: w, height: h };
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('Could not resize photo'))), 'image/jpeg', 0.85),
  );
  return { blob, width: w, height: h };
}

export type UploadOptions = {
  siteId: string;
  bucket?: 'documents' | 'photos' | 'drawings';
  keepOriginal?: boolean;
  title?: string;
  linkedTable?: string;
  linkedId?: string;
  commentId?: string;
  /** id of the file this upload is a new version of */
  replaces?: string;
  notes?: string;
};

export async function uploadFile(file: File, o: UploadOptions) {
  const isImage = /^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type);
  const bucket = o.bucket ?? (isImage ? 'photos' : 'documents');

  let body: Blob = file;
  let name = file.name;
  let width: number | undefined;
  let height: number | undefined;
  let mime = file.type || 'application/octet-stream';
  if (isImage && !o.keepOriginal) {
    try {
      const r = await resizeImage(file);
      body = r.blob;
      width = r.width;
      height = r.height;
      if (r.blob !== file) {
        mime = 'image/jpeg';
        name = name.replace(/\.(png|webp|heic|heif|jpe?g)$/i, '') + '.jpg';
      }
    } catch {
      /* browser can't decode (e.g. HEIC on desktop) — upload as is */
    }
  }

  const hash = await sha256(body);
  const up = await api<{ bucket: string; path: string; token: string }>('upload-url', {
    site_id: o.siteId,
    bucket,
    filename: name,
  });
  const { error } = await supabase.storage.from(up.bucket).uploadToSignedUrl(up.path, up.token, body, { contentType: mime });
  if (error) throw new Error(error.message);

  return api<{ row: Record<string, unknown> }>('file-commit', {
    site_id: o.siteId,
    bucket: up.bucket,
    path: up.path,
    original_name: name,
    title: o.title,
    mime,
    size_bytes: body.size,
    sha256: hash,
    width,
    height,
    taken_at: file.lastModified ? new Date(file.lastModified).toISOString() : undefined,
    original_kept: Boolean(o.keepOriginal),
    linked_table: o.linkedTable,
    linked_id: o.linkedId,
    comment_id: o.commentId,
    replaces: o.replaces,
    notes: o.notes,
  });
}
